import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import ReplayIcon from "@mui/icons-material/Replay";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { LiveTrainingClubLogo } from "./LiveTrainingClubLogo";

/**
 * Shown when every training exercise is terminal and the session is not yet closed.
 * Coach: finalize session, relaunch an exercise, or add another exercise.
 * Player: wait for the coach.
 */
export function LiveTrainingSessionDecisionPanel({
  user,
  isCoachView,
  onFinalizeSession,
  onAddExercise,
  onRestartLastExercise = null,
  onRelaunchPreviousExercise = null,
  hasPreviousExercise = false,
  finalizing = false,
  relaunching = false,
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const actionsDisabled = busy || finalizing || relaunching;

  const handleFinalize = async () => {
    if (actionsDisabled) return;
    setBusy(true);
    try {
      await onFinalizeSession?.();
    } finally {
      setBusy(false);
    }
  };

  if (!isCoachView) {
    return (
      <div className="lt-session-decision" role="status" aria-live="polite">
        <div className="lt-session-decision__glow" aria-hidden />
        <LiveTrainingClubLogo
          user={user}
          size="lg"
          className="lt-session-decision__club-logo"
          showName
        />
        <HourglassTopIcon className="lt-session-decision__icon" aria-hidden />
        <h2 className="lt-session-decision__title">
          {t("liveTrainingAwaitingCoachDecisionTitle")}
        </h2>
        <p className="lt-session-decision__message">
          {t("liveTrainingAwaitingCoachDecisionMessage")}
        </p>
      </div>
    );
  }

  return (
    <div className="lt-session-decision" role="dialog" aria-modal="true">
      <div className="lt-session-decision__glow" aria-hidden />
      <LiveTrainingClubLogo
        user={user}
        size="lg"
        className="lt-session-decision__club-logo"
        showName
      />
      <CheckCircleOutlineIcon className="lt-session-decision__icon" aria-hidden />
      <h2 className="lt-session-decision__title">
        {t("liveTrainingSessionDecisionTitle")}
      </h2>
      <p className="lt-session-decision__message">
        {t("liveTrainingSessionDecisionMessage")}
      </p>
      <div className="lt-session-decision__actions">
        {onRestartLastExercise ? (
          <button
            type="button"
            className="lt-session-decision__btn lt-session-decision__btn--secondary"
            onClick={onRestartLastExercise}
            disabled={actionsDisabled}
          >
            <ReplayIcon fontSize="small" aria-hidden />
            {relaunching
              ? t("loading") || "…"
              : t("liveTrainingRestartExercise")}
          </button>
        ) : null}
        {hasPreviousExercise && onRelaunchPreviousExercise ? (
          <button
            type="button"
            className="lt-session-decision__btn lt-session-decision__btn--secondary"
            onClick={onRelaunchPreviousExercise}
            disabled={actionsDisabled}
          >
            <NavigateBeforeIcon fontSize="small" aria-hidden />
            {relaunching
              ? t("loading") || "…"
              : t("liveTrainingPreviousExercise")}
          </button>
        ) : null}
        <button
          type="button"
          className="lt-session-decision__btn lt-session-decision__btn--primary"
          onClick={handleFinalize}
          disabled={actionsDisabled}
        >
          <CheckCircleOutlineIcon fontSize="small" aria-hidden />
          {busy || finalizing
            ? t("liveTrainingSessionFinalizing")
            : t("liveTrainingFinalizeSession")}
        </button>
        <button
          type="button"
          className="lt-session-decision__btn lt-session-decision__btn--secondary"
          onClick={onAddExercise}
          disabled={actionsDisabled}
        >
          <AddCircleOutlineIcon fontSize="small" aria-hidden />
          {t("liveTrainingAddExercise")}
        </button>
      </div>
    </div>
  );
}

export default LiveTrainingSessionDecisionPanel;
